import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import type { Remirror as RemirrorTypes } from '@trinity/chat';
import type { ResearchQuoteWidgetData } from '@trinity/chat/types/dashboard-data';
import { useRemirrorEditor } from '../../Remirror/BasicMarkupEditor.js';
import { WidgetQuoteCardCompact } from '../../Widget.Quote/Widget.Quote.Card.Compact.js';
import { WidgetSourcesPane, type WidgetSourceRef } from './WidgetSources.js';

export { QuoteSourceLinkProvider, type QuoteSourceLinkResolver } from './WidgetSources.js';

/** A research quote: the compact quote card with its media clip, plus a collapsible Sources pane. */
export function ResearchQuote({ data }: { data: ResearchQuoteWidgetData }): React.ReactElement {
  const { state } = useRemirrorEditor({ content: data.quote as RemirrorTypes.RootNode });
  const text = state.doc.textContent;
  const sources = useMemo<WidgetSourceRef[]>(
    () => [{ kind: 'transcript', source: data.source, speaker: data.speaker, text }],
    [data.source, data.speaker, text],
  );

  // The quote fills whatever height its cell has, with Sources pinned below it. A quote with no real
  // transcript (a library quote copied in without a source) has no Sources.
  return (
    <QuoteRoot>
      <WidgetQuoteCardCompact
        quote={data.quote as RemirrorTypes.RootNode}
        insights={data.insights as RemirrorTypes.RootNode}
        media={data.media as never}
      />
      {data.source.transcriptId ? (
        <SourceRoot>
          <WidgetSourcesPane sources={sources} />
        </SourceRoot>
      ) : null}
    </QuoteRoot>
  );
}

const QuoteRoot = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
});

const SourceRoot = styled.div({
  borderTop: '1px solid rgba(0,0,0,0.08)',
  flexShrink: 0,
});
